import { renderToStaticMarkup } from 'react-dom/server';
import { FaQuoteLeft } from 'react-icons/fa';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { linkifyBareUrlsInDisplayHtml } from '../CommentsNReplies/commentLinkUtils';

export function escapeHtml(input = '') {
  return input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** Placeholder for pipe tables (must not match markdown `__underline__` or other rules). */
function markdownTablePlaceholder(i) {
  return `\u2060WFMTAB${i}\u2060`;
}

/** Placeholder for fenced ``` code blocks (extracted before newline/paragraph pass). */
function markdownFencedCodePlaceholder(i) {
  return `\u2060WFMCODE${i}\u2060`;
}

function extractFencedCodeBlocksForMarkdownToHtml(raw) {
  const codes = [];
  const text = String(raw).replace(/```([\s\S]*?)```/g, (_match, inner) => {
    const idx = codes.length;
    codes.push(String(inner ?? ''));
    return `\n\n${markdownFencedCodePlaceholder(idx)}\n\n`;
  });
  return { text, codes };
}

function splitPipeTableRow(line) {
  let s = line.trim();
  if (!s.includes('|')) return [];
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function isMarkdownPipeSeparatorLine(line) {
  return /^\s*\|[\s\-:|]+\|\s*$/.test(line);
}

function isMarkdownPipeDataLine(line) {
  const t = line.trim();
  return t.startsWith('|') && /\|[^|]/.test(t);
}

function pipeTableLinesToHtmlTable(blockLines) {
  if (blockLines.length < 2) return '';
  const header = splitPipeTableRow(blockLines[0]);
  if (!header.length) return '';
  const bodyRows = blockLines.slice(2).map(splitPipeTableRow).filter((r) => r.length);
  const ncol = Math.max(header.length, ...bodyRows.map((r) => r.length), 0);
  if (!ncol) return '';
  const pad = (cells) => {
    const a = cells.slice();
    while (a.length < ncol) a.push('');
    return a;
  };
  const styleAttr = 'border-collapse:collapse;box-sizing:border-box;width:100%;max-width:100%';
  const ths = pad(header)
    .map((c) => `<th>${escapeHtml(c)}</th>`)
    .join('');
  const tbody = bodyRows
    .map((row) => {
      const tds = pad(row)
        .map((c) => `<td>${escapeHtml(c)}</td>`)
        .join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');
  return `<table class="wfm-text-editor__table" style="${styleAttr}"><thead><tr>${ths}</tr></thead><tbody>${tbody}</tbody></table>`;
}

/**
 * Pull GFM-style pipe tables out of raw markdown so the rest of the pipeline can run;
 * tables are re-inserted as HTML after conversion.
 */
function extractPipeTablesForMarkdownToHtml(raw) {
  const tablesHtml = [];
  const lines = raw.split(/\r\n|\r|\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    if (i + 1 < lines.length && isMarkdownPipeDataLine(lines[i]) && isMarkdownPipeSeparatorLine(lines[i + 1])) {
      let end = i + 2;
      while (end < lines.length && isMarkdownPipeDataLine(lines[end])) end++;
      const block = lines.slice(i, end);
      const tbl = pipeTableLinesToHtmlTable(block);
      if (tbl) {
        tablesHtml.push(tbl);
        out.push(markdownTablePlaceholder(tablesHtml.length - 1));
        i = end;
        continue;
      }
    }
    out.push(lines[i]);
    i++;
  }
  return { text: out.join('\n'), tablesHtml };
}
function mentionMailtoAnchorHtml(displayText, email) {
  const em = String(email || '').trim();
  const safeD = escapeHtml(displayText);
  if (!em) {
    return `<span class="wfm-mention wfm-mention-display">${safeD}</span>`;
  }
  const title = escapeHtml(em);
  const href = `mailto:${encodeURIComponent(em)}`;
  return `<a href="${href}" class="wfm-mention wfm-mention--link" title="${title}">${safeD}</a>`;
}

/** Legacy/plain @mentions (not `[@Name](mailto:…)`). Skips text inside tags when splitting. */
function wrapMentionsInCompletedHtml(html) {
  if (!html || !html.includes('@')) return html;
  return html
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (part.startsWith('<') || !part.includes('@')) return part;
      let t = part;
      t = t.replace(/@([^\s@]+@[^\s@]+\.[^\s@]+)/g, (full, emailInner) => {
        const local = String(emailInner).split('@')[0] || emailInner;
        return mentionMailtoAnchorHtml(`@${local}`, emailInner);
      });
      t = t.replace(
        /@([a-zA-Z][a-zA-Z0-9._-]{0,63})(?!@)/g,
        '<span class="wfm-mention wfm-mention-display">@$1</span>'
      );
      return t;
    })
    .join('');
}

/**
 * `escapeHtml` turns leading `>` into `&gt;`, so `^\> ` never matched; merge consecutive quoted lines into one blockquote.
 */
function markdownEscapedBlockquoteLinesToHtml(html) {
  const lines = html.split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const m = /^&gt;\s?(.*)$/.exec(lines[i]);
    if (m) {
      const inners = [];
      while (i < lines.length) {
        const m2 = /^&gt;\s?(.*)$/.exec(lines[i]);
        if (!m2) break;
        inners.push(m2[1]);
        i++;
      }
      out.push(`<blockquote>${inners.join('<br/>')}</blockquote>`);
      continue;
    }
    out.push(lines[i]);
    i++;
  }
  return out.join('\n');
}

export function markdownToHtml(markdown = '') {
  let raw = String(markdown);
  const mentionBlocks = [];
  raw = raw.replace(/\[@([^\]]+)\]\(mailto:([^)]+)\)/gi, (full, namePart, em) => {
    const idx = mentionBlocks.length;
    const first =
      String(namePart || '')
        .trim()
        .split(/\s+/)[0] || String(em).split('@')[0] || 'user';
    const display = `@${first.replace(/^@/, '')}`;
    mentionBlocks.push({ display, email: String(em).trim() });
    return `\u2060WFMMT${idx}\u2060`;
  });

  const { text: rawWithoutPipeTables, tablesHtml } = extractPipeTablesForMarkdownToHtml(raw);
  raw = rawWithoutPipeTables;

  const { text: rawWithoutFencedCode, codes: fencedCodes } = extractFencedCodeBlocksForMarkdownToHtml(raw);
  raw = rawWithoutFencedCode;

  let html = escapeHtml(raw);

  html = html
    .replace(/^###### (.*)$/gm, '<h6>$1</h6>')
    .replace(/^##### (.*)$/gm, '<h5>$1</h5>')
    .replace(/^#### (.*)$/gm, '<h4>$1</h4>')
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>');

  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  html = markdownEscapedBlockquoteLinesToHtml(html);
  html = html.replace(/^- (.*)$/gm, '<li data-list="ul">$1</li>');
  html = html.replace(/^\d+\. (.*)$/gm, '<li data-list="ol">$1</li>');

  html = html
    .replace(/(?:\r\n|\r|\n){2,}/g, '</p><p>')
    .replace(/(?:\r\n|\r|\n)/g, '<br/>');

  html = `<p>${html}</p>`
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(\s*)<\/p>/g, '<p><br/></p>');

  /* Paragraph splitting can wrap blockquotes in <p>, which browsers fix poorly in contenteditable */
  html = html.replace(/<p><blockquote>/g, '<blockquote>').replace(/<\/blockquote><\/p>/g, '</blockquote>');

  html = html
    .replace(/(<li data-list="ul">[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/(<li data-list="ol">[\s\S]*?<\/li>)/g, '<ol>$1</ol>')
    .replace(/<\/ol>\s*<ol>/g, '')
    .replaceAll(' data-list="ul"', '')
    .replaceAll(' data-list="ol"', '');

  for (let i = 0; i < mentionBlocks.length; i++) {
    const { display, email } = mentionBlocks[i];
    html = html.replace(`\u2060WFMMT${i}\u2060`, mentionMailtoAnchorHtml(display, email));
  }

  for (let i = 0; i < tablesHtml.length; i++) {
    const ph = markdownTablePlaceholder(i);
    if (html.includes(ph)) {
      html = html.split(ph).join(tablesHtml[i]);
    }
  }

  for (let i = 0; i < fencedCodes.length; i++) {
    const ph = markdownFencedCodePlaceholder(i);
    if (html.includes(ph)) {
      html = html.split(ph).join(`<pre><code>${escapeHtml(fencedCodes[i])}</code></pre>`);
    }
  }

  html = wrapMentionsInCompletedHtml(html);

  return html;
}

/** Static HTML for FaQuoteLeft (one icon per blockquote, not per line). */
const COMMENT_BLOCKQUOTE_ICON_HTML = `<span class="support-comment-quote__glyph">${renderToStaticMarkup(
  <FaQuoteLeft aria-hidden="true" focusable="false" />
)}</span>`;

function mergeBlockquoteClassAttr(rawAttrs, extraClass) {
  const a = String(rawAttrs || '').trim();
  if (!a) return ` class="${extraClass}"`;
  const m = /\bclass\s*=\s*(["'])((?:\\\1|.)*?)\1/i.exec(a);
  if (m) {
    const q = m[1];
    const nextClasses = `${m[2]} ${extraClass}`.trim();
    const replaced = a.replace(m[0], `class=${q}${nextClasses}${q}`);
    return replaced.startsWith(' ') ? replaced : ` ${replaced}`;
  }
  return ` ${a} class="${extraClass}"`;
}

/**
 * Wrap blockquotes for comment display: leading quote icon once (FaQuoteLeft), body flows beside/wrapped.
 */
function wrapCommentBlockquotesForDisplay(html) {
  return String(html || '').replace(
    /<blockquote([^>]*)>([\s\S]*?)<\/blockquote>/gi,
    (_, attrs, inner) => {
      const merged = mergeBlockquoteClassAttr(attrs, 'support-comment-quote__inner');
      return `<div class="support-comment-quote">${COMMENT_BLOCKQUOTE_ICON_HTML}<blockquote${merged}>${inner}</blockquote></div>`;
    }
  );
}

/**
 * Wrap top-level <table> elements so wide grids can scroll horizontally on narrow viewports (see CommentsNReplies.css).
 * Skips tables nested inside another table.
 */
function wrapCommentTablesForDisplayString(html) {
  const s = String(html || '');
  if (!s.includes('<table')) return s;
  if (/<table\b[^>]*>[\s\S]*?<table\b/i.test(s)) return s;
  return s.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (m) => {
    if (m.includes('support-comment-table-wrap')) return m;
    return `<div class="support-comment-table-wrap">${m}</div>`;
  });
}

function wrapCommentTablesForDisplay(html) {
  const s = String(html || '');
  if (!s.includes('<table')) return s;
  if (typeof document === 'undefined') return wrapCommentTablesForDisplayString(s);
  try {
    const container = document.createElement('div');
    container.innerHTML = s;
    container.querySelectorAll('table').forEach((table) => {
      if (table.closest('.support-comment-table-wrap')) return;
      if (table.parentElement?.closest('table')) return;
      const wrap = document.createElement('div');
      wrap.className = 'support-comment-table-wrap';
      table.replaceWith(wrap);
      wrap.appendChild(table);
    });
    return container.innerHTML;
  } catch {
    return wrapCommentTablesForDisplayString(s);
  }
}

/** Tags/contenteditable output that indicates stored rich HTML (not markdown mentioning `<tag>`). */
const STORED_RICH_HTML_TAG_RE =
  /<\s*(div|br|p|span|strong|em|b|i|u|ul|ol|li|blockquote|h[1-6]|img|video|audio|table|figure|pre|a|hr)\b/i;

/**
 * Whether a stored comment body is HTML saved from the rich editor (vs legacy markdown/plain).
 * Used to choose display pipeline and TextEditor initial props.
 */
export function commentBodyLooksLikeStoredHtml(s) {
  const t = String(s || '').trim();
  if (!t || !t.includes('<')) return false;
  if (/^<\s*[a-z]/i.test(t) || /^<\s*\/\s*[a-z]/i.test(t)) return true;
  // contenteditable often leaves a leading text node before block tags (e.g. "Hello<div><br></div>").
  return STORED_RICH_HTML_TAG_RE.test(t);
}

const NBSP_CHAR = '\u00a0';
const SPACE_LIKE_RE = /(?:&nbsp;|&#160;|\u00a0|\s)/gi;

/** HTML collapses consecutive regular spaces; store intentional runs of 2+ as nbsp characters. */
function encodeMultipleSpaces(text) {
  return String(text || '').replace(/ {2,}/g, (run) => NBSP_CHAR.repeat(run.length));
}

function unifyNbspEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, NBSP_CHAR)
    .replace(/&#160;/gi, NBSP_CHAR);
}

/**
 * Fix spaced punctuation artifacts from contenteditable (e.g. `.. ..` → `..`).
 * Keep `&nbsp;` / nbsp characters — do not convert them to regular spaces (HTML would collapse them).
 */
function normalizeCommentBodyTextSegment(text) {
  let t = unifyNbspEntities(text);
  const isWhitespaceOnly = !t.replace(SPACE_LIKE_RE, '');

  if (isWhitespaceOnly) return encodeMultipleSpaces(t);

  t = t
    .replace(/(\.{2,})(?:&nbsp;|&#160;|\u00a0|\s)+(\.{2,})/gi, '$1')
    .replace(/(\.)(?:&nbsp;|&#160;|\u00a0|\s)+(\.)/g, '$1$2')
    .replace(/(?:&nbsp;|&#160;|\u00a0|\s)+(?=[.,!?;:])/gi, '');

  return encodeMultipleSpaces(t);
}

export const EDITOR_THEME_SURFACE_BG = {
  light: '#ffffff',
  dark: '#131316',
};

export const EDITOR_THEME_TEXT_COLORS = {
  light: '#111827',
  dark: '#ececed',
};

export const RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS = 'wfm-text-editor__color-theme-adapted';

const EDITOR_MIN_TEXT_CONTRAST_RATIO = 4.5;

let cssColorProbeEl;

function srgbChannelToLinear(channel) {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function relativeLuminanceFromRgb([r, g, b]) {
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  );
}

function contrastRatioBetweenRgb(rgbA, rgbB) {
  const lumA = relativeLuminanceFromRgb(rgbA);
  const lumB = relativeLuminanceFromRgb(rgbB);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

export function parseCssColorToRgb(color) {
  const raw = String(color || '').trim();
  if (!raw || typeof document === 'undefined') return null;

  if (!cssColorProbeEl) {
    cssColorProbeEl = document.createElement('span');
    cssColorProbeEl.style.display = 'none';
    document.documentElement.appendChild(cssColorProbeEl);
  }

  cssColorProbeEl.style.color = '';
  cssColorProbeEl.style.color = raw;
  if (!cssColorProbeEl.style.color) return null;

  const computed = window.getComputedStyle(cssColorProbeEl).color;
  const match = computed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function getEditorThemeDefaultTextColor(theme) {
  return EDITOR_THEME_TEXT_COLORS[theme === 'dark' ? 'dark' : 'light'];
}

function resolveEditorSurfaceBackground(el) {
  if (!(el instanceof HTMLElement) || typeof window === 'undefined') return null;
  let node = el;
  while (node && node instanceof HTMLElement) {
    const bg = window.getComputedStyle(node).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      return bg;
    }
    node = node.parentElement;
  }
  return null;
}

export function getEditorColorContrastRatio(color, theme, surfaceBgColor = null) {
  const textRgb = parseCssColorToRgb(color);
  const resolvedBg =
    surfaceBgColor ||
    EDITOR_THEME_SURFACE_BG[theme === 'dark' ? 'dark' : 'light'];
  const bgRgb = parseCssColorToRgb(resolvedBg);
  if (!textRgb || !bgRgb) return null;
  return contrastRatioBetweenRgb(textRgb, bgRgb);
}

export function editorInlineColorNeedsThemeAdaptation(color, theme, surfaceBgColor = null) {
  const ratio = getEditorColorContrastRatio(color, theme, surfaceBgColor);
  if (ratio == null) return false;
  return ratio < EDITOR_MIN_TEXT_CONTRAST_RATIO;
}

export function getInlineColorFromElement(el) {
  if (!(el instanceof HTMLElement)) return null;
  const attrColor = el.getAttribute('color');
  if (attrColor) return attrColor;
  const inlineColor = el.style?.color;
  if (inlineColor) return inlineColor;
  return null;
}

export function stripRichEditorThemeColorAdaptation(html) {
  return String(html || '').replace(
    new RegExp(`\\s*${RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS}\\b`, 'g'),
    ''
  );
}

export function applyRichEditorThemeColorAdaptation(rootEl, theme) {
  if (!rootEl || typeof document === 'undefined') return;
  const resolvedTheme = theme === 'dark' ? 'dark' : 'light';
  const surfaceBgColor =
    resolveEditorSurfaceBackground(rootEl) ||
    EDITOR_THEME_SURFACE_BG[resolvedTheme];

  rootEl.querySelectorAll('[style*="color"], font[color]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.closest('.wfm-mention, a.wfm-mention')) return;

    const color = getInlineColorFromElement(node);
    if (!color) {
      node.classList.remove(RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS);
      return;
    }

    if (editorInlineColorNeedsThemeAdaptation(color, resolvedTheme, surfaceBgColor)) {
      node.classList.add(RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS);
    } else {
      node.classList.remove(RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS);
    }
  });
}

export function adaptStoredHtmlInlineColorsForTheme(html, theme) {
  const raw = String(html || '');
  if (!raw.trim() || typeof document === 'undefined') return raw;
  try {
    const container = document.createElement('div');
    container.innerHTML = raw;
    applyRichEditorThemeColorAdaptation(container, theme);
    return container.innerHTML;
  } catch {
    return raw;
  }
}

/**
 * Normalize stored comment bodies before save/display (rich HTML or plain/markdown text).
 */
export function normalizeCommentBodyArtifacts(body) {
  const raw = String(body ?? '');
  if (!raw) return raw;
  if (commentBodyLooksLikeStoredHtml(raw)) {
    return stripRichEditorThemeColorAdaptation(
      raw
        .split(/(<[^>]+>)/g)
        .map((part) => (part.startsWith('<') ? part : normalizeCommentBodyTextSegment(part)))
        .join('')
    );
  }
  return normalizeCommentBodyTextSegment(raw);
}

/**
 * HTML for dangerouslySetInnerHTML: legacy markdown/plain → markdownToHtml; stored rich HTML → as-is + mention pass.
 */
const DISCUSSION_BODY_WEBKIT_PASTE_STYLE_PROPS = [
  'font-variant-caps',
  'font-width',
  'font-size-adjust',
  'font-kerning',
  'font-variant-alternates',
  'font-variant-ligatures',
  'font-variant-numeric',
  'font-variant-east-asian',
  'font-variant-position',
  'font-feature-settings',
  'font-optical-sizing',
  'font-variation-settings',
];

const DISCUSSION_BODY_BLOCK_TAGS = new Set(['P', 'DIV', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

function stripDiscussionBodyDisplayInlineStyles(el) {
  if (!(el instanceof HTMLElement)) return;
  DISCUSSION_BODY_WEBKIT_PASTE_STYLE_PROPS.forEach((prop) => el.style.removeProperty(prop));
  el.style.removeProperty('padding-left');
  el.style.removeProperty('padding-right');
  el.style.removeProperty('margin-left');
  el.style.removeProperty('text-indent');
  if (DISCUSSION_BODY_BLOCK_TAGS.has(el.tagName)) {
    el.style.removeProperty('font-size');
    el.style.removeProperty('line-height');
  }
  if (el.tagName === 'A' && !el.classList.contains('wfm-mention')) {
    el.style.removeProperty('font-size');
    el.style.removeProperty('line-height');
    el.style.removeProperty('font-weight');
  }
  if (!String(el.getAttribute('style') || '').trim()) el.removeAttribute('style');
}

function renameDomElement(document, el, tagName) {
  if (!el || el.tagName.toLowerCase() === String(tagName).toLowerCase()) return el;
  const next = document.createElement(tagName);
  Array.from(el.attributes).forEach((attr) => next.setAttribute(attr.name, attr.value));
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

/** Keep hashtag chips inside <p> by using phrasing tags only (divs get hoisted by the HTML parser). */
function convertHashtagBoxesToInlineSpans(container, document = container?.ownerDocument) {
  if (!container || !document) return;
  Array.from(container.querySelectorAll('.support-open-disc-hashtag-box')).forEach((box) => {
    const converted = renameDomElement(document, box, 'span');
    Array.from(converted.querySelectorAll('div')).forEach((inner) => renameDomElement(document, inner, 'span'));
  });
}

/** Unwrap invalid inline wrappers and normalize contenteditable line `<div>`s. */
export function repairDiscussionBodyHtml(html) {
  const raw = String(html ?? '');
  if (!raw.trim() || !commentBodyLooksLikeStoredHtml(raw)) return raw;
  if (typeof document === 'undefined') return raw;
  try {
    const container = document.createElement('div');
    container.innerHTML = raw;

    const isProtectedDiv = (el) => {
      const cls = String(el.className || '');
      return /wfm-text-editor__video-wrap|support-comment-table-wrap|support-comment-quote|support-open-disc-hashtag-box/.test(
        cls
      );
    };

    container.querySelectorAll('span[style], font').forEach((wrapper) => {
      const hasBlockChild = Array.from(wrapper.childNodes).some(
        (n) =>
          n.nodeType === Node.ELEMENT_NODE &&
          /^(DIV|P|H[1-6]|BLOCKQUOTE|PRE|UL|OL|LI|TABLE)$/i.test(n.tagName)
      );
      if (!hasBlockChild) return;
      const parent = wrapper.parentNode;
      if (!parent) return;
      Array.from(wrapper.childNodes).forEach((ch) => parent.insertBefore(ch, wrapper));
      wrapper.remove();
    });

    Array.from(container.querySelectorAll('div')).forEach((div) => {
      if (isProtectedDiv(div)) return;
      if (
        div.closest(
          '.support-comment-quote, .wfm-text-editor__video-wrap, .support-comment-table-wrap, .support-open-disc-hashtag-box'
        )
      ) {
        return;
      }
      if (div.querySelector('table, .wfm-text-editor__video-wrap, .support-comment-quote')) return;
      const p = document.createElement('p');
      while (div.firstChild) p.appendChild(div.firstChild);
      div.replaceWith(p);
    });

    container.querySelectorAll('p, div, span, font, blockquote, a').forEach((el) => {
      if (el.closest('.support-comment-quote__glyph, .wfm-mention')) return;
      if (isProtectedDiv(el)) return;
      stripDiscussionBodyDisplayInlineStyles(el);
    });

    convertHashtagBoxesToInlineSpans(container);

    return container.innerHTML;
  } catch {
    return raw;
  }
}

export function resolveAppTheme() {
  return typeof document !== 'undefined' && document.documentElement?.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light';
}

const POST_BODY_PARAGRAPH_SKIP_SELECTOR =
  '.support-comment-quote, blockquote, .support-comment-table-wrap, td, th, figcaption';

function paragraphShouldNormalizeParagraph(p) {
  if (!(p instanceof HTMLElement) || p.tagName !== 'P') return false;
  return !p.closest(POST_BODY_PARAGRAPH_SKIP_SELECTOR);
}

function paragraphVisibleText(p) {
  const clone = p.cloneNode(true);
  clone.querySelectorAll('br').forEach((br) => br.remove());
  return String(clone.textContent || '').replace(/\u00a0/g, '').trim();
}

function isPostBodyMediaParagraph(p) {
  return (
    paragraphShouldNormalizeParagraph(p) &&
    Boolean(p.querySelector('img, video, figure, .wfm-text-editor__video-wrap, audio'))
  );
}

function isPostBodyContentParagraph(p) {
  return paragraphShouldNormalizeParagraph(p) && (isPostBodyMediaParagraph(p) || paragraphVisibleText(p).length > 0);
}

function isPostBodyEmptyParagraph(p) {
  return paragraphShouldNormalizeParagraph(p) && !paragraphVisibleText(p);
}

/** Editor line-break placeholder (<p><br></p>), not an intentional empty paragraph. */
function isPostBodyBrOnlyPlaceholderParagraph(p) {
  if (!isPostBodyEmptyParagraph(p)) return false;
  if (p.classList?.contains('wfm-text-editor__post-media-p')) return false;
  if (isPostBodyMediaParagraph(p)) return false;
  if (p.childNodes.length === 0) return false;
  return p.querySelector('br') != null;
}

function stripTrailingBrBeforeNextParagraph(p) {
  if (p.nextElementSibling?.tagName !== 'P') return;
  while (p.lastChild) {
    const last = p.lastChild;
    if (last.nodeType === Node.TEXT_NODE && !String(last.textContent || '').trim()) {
      p.removeChild(last);
      continue;
    }
    if (last.nodeType === Node.ELEMENT_NODE && last.tagName === 'BR') {
      p.removeChild(last);
      continue;
    }
    break;
  }
}

function removeBrPlaceholderParagraphsBetweenContent(parent) {
  if (!(parent instanceof HTMLElement)) return;
  const blocks = Array.from(parent.children);
  for (let i = 0; i < blocks.length; i++) {
    const node = blocks[i];
    if (!isPostBodyContentParagraph(node)) {
      if (node instanceof HTMLElement && /^(DIV|UL|OL|BLOCKQUOTE)$/.test(node.tagName)) {
        removeBrPlaceholderParagraphsBetweenContent(node);
      }
      continue;
    }
    let j = i + 1;
    while (j < blocks.length && isPostBodyBrOnlyPlaceholderParagraph(blocks[j])) j += 1;
    if (j > i + 1 && j < blocks.length && isPostBodyContentParagraph(blocks[j])) {
      for (let k = j - 1; k > i; k -= 1) blocks[k].remove();
    }
  }
}

/** Normalize editor paragraph breaks for save + display (opened posts). */
export function normalizePostBodyParagraphBreaksForDisplay(html) {
  const raw = String(html ?? '');
  if (!raw.trim() || typeof document === 'undefined') return raw;
  try {
    const container = document.createElement('div');
    container.innerHTML = raw;
    container.querySelectorAll('p').forEach((p) => {
      if (!paragraphShouldNormalizeParagraph(p)) return;
      stripTrailingBrBeforeNextParagraph(p);
    });
    removeBrPlaceholderParagraphsBetweenContent(container);
    return container.innerHTML;
  } catch {
    return raw;
  }
}

export function commentBodyToDisplayHtml(body, theme = resolveAppTheme()) {
  const raw = normalizeCommentBodyArtifacts(repairDiscussionBodyHtml(String(body ?? '')));
  let html;
  if (commentBodyLooksLikeStoredHtml(raw)) {
    html = wrapMentionsInCompletedHtml(raw);
  } else {
    html = markdownToHtml(raw);
  }
  html = sanitizeHtml(html);
  html = normalizePostBodyParagraphBreaksForDisplay(html);
  html = linkifyBareUrlsInDisplayHtml(html);
  html = wrapCommentBlockquotesForDisplay(html);
  html = wrapCommentTablesForDisplay(html);
  return adaptStoredHtmlInlineColorsForTheme(html, theme);
}

/** Root-relative /uploads paths for display (strip stale absolute API host). */
function defaultResolveMediaAssetUrl(raw) {
  const u = String(raw || '').trim();
  if (!u) return '';
  if (u.startsWith('//')) return `https:${u}`;
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      /* ignore */
    }
    return u;
  }
  return `/${u.replace(/^\//, '')}`;
}

export function resolveAssetUrlsInHtml(html, resolveUrl = defaultResolveMediaAssetUrl) {
  const raw = String(html || '');
  if (!raw.trim() || typeof document === 'undefined') return raw;
  if (!/<img\b|<video\b/i.test(raw)) return raw;
  try {
    const container = document.createElement('div');
    container.innerHTML = raw;
    container.querySelectorAll('img[src]').forEach((el) => {
      const resolved = resolveUrl(el.getAttribute('src') || '');
      if (resolved) el.setAttribute('src', resolved);
    });
    container.querySelectorAll('video[src]').forEach((el) => {
      const resolved = resolveUrl(el.getAttribute('src') || '');
      if (resolved) el.setAttribute('src', resolved);
      const poster = el.getAttribute('poster');
      if (poster) {
        const resolvedPoster = resolveUrl(poster);
        if (resolvedPoster) el.setAttribute('poster', resolvedPoster);
      }
    });
    container.querySelectorAll('source[src]').forEach((el) => {
      const resolved = resolveUrl(el.getAttribute('src') || '');
      if (resolved) el.setAttribute('src', resolved);
    });
    return container.innerHTML;
  } catch {
    return raw;
  }
}

/** Rich HTML with no visible text but real editor content (lists, quotes, code, media, etc.). */
const RICH_HTML_STRUCTURE_RE =
  /<(img|video|audio|iframe|table|figure|svg|pre|blockquote|ul|ol|hr|h[1-6])\b/i;

function richHtmlHasStructuralContent(html) {
  const t = String(html || '');
  if (RICH_HTML_STRUCTURE_RE.test(t)) return true;
  if (/<div[^>]*\bwfm-text-editor__video-wrap\b/i.test(t)) return true;
  if (/<a\s[^>]*\bhref\s*=/i.test(t)) return true;
  return false;
}

/** Media embedded in rich comment/post HTML (figures / video wraps), in document order. */
export function extractBodyMediaFromHtml(html, resolveUrl = defaultResolveMediaAssetUrl) {
  const items = [];
  const seen = new Set();
  const raw = String(html || '').trim();
  if (!raw || typeof document === 'undefined') return items;

  const add = (item) => {
    const url = String(item?.url || '').trim();
    if (!url) return;
    const key = `${item.type}:${url}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({
      url,
      type: item.type === 'video' ? 'video' : 'image',
      posterUrl: String(item.posterUrl || '').trim(),
      alt: String(item.alt || '').trim(),
    });
  };

  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const root = doc.body;
  if (!root) return items;

  root.querySelectorAll('figure').forEach((figure) => {
    const img = figure.querySelector('img[src]');
    if (!img) return;
    add({
      url: resolveUrl(img.getAttribute('src') || ''),
      type: 'image',
      posterUrl: '',
      alt: img.getAttribute('alt') || '',
    });
  });

  root.querySelectorAll('.wfm-text-editor__video-wrap video[src]').forEach((video) => {
    const poster = video.getAttribute('poster') || '';
    add({
      url: resolveUrl(video.getAttribute('src') || ''),
      type: 'video',
      posterUrl: poster ? resolveUrl(poster) : '',
      alt: '',
    });
  });

  root.querySelectorAll('img[src]').forEach((img) => {
    if (img.closest('figure')) return;
    add({
      url: resolveUrl(img.getAttribute('src') || ''),
      type: 'image',
      posterUrl: '',
      alt: img.getAttribute('alt') || '',
    });
  });

  return items;
}

/** Match stored modal URL to a live img/video src attribute (raw or resolved). */
export function bodyMediaSrcMatches(itemUrl, attrSrc, resolveUrl = defaultResolveMediaAssetUrl) {
  const raw = String(attrSrc || '').trim();
  const resolved = String(itemUrl || '').trim();
  if (!raw || !resolved) return false;
  const rawResolved = resolveUrl(raw);
  return resolved === raw || resolved === rawResolved || resolveUrl(resolved) === rawResolved;
}

/**
 * True if the user has not entered meaningful content (handles empty rich-editor HTML and image-only bodies).
 */
export function commentBodyIsEffectivelyEmpty(s) {
  const t = String(s || '').trim();
  if (!t) return true;
  if (commentBodyLooksLikeStoredHtml(t)) {
    if (richHtmlHasStructuralContent(t)) return false;
    const plain = t
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<(br|hr)\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&[a-z]+;/gi, ' ')
      .trim();
    return !plain;
  }
  return !t.replace(/\s+/g, '');
}
